import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import {
  CM_ACTIONS,
  CM_INSIGHT_CHIPS,
  CM_INSIGHTS,
  CM_QUICK_WINS,
  CM_RAW,
  CM_RECOVERABLE_BANNER,
  CIRCLE_COLORS,
} from '../../data/cmData';
import {
  CM_FEB_ACTIONS,
  CM_FEB_DEC_INTERCITY_AVG,
  CM_FEB_INSIGHT_CHIPS,
  CM_FEB_INSIGHTS,
  CM_FEB_MPS,
  CM_FEB_QUICK_WINS,
  CM_FEB_RECOVERABLE_BANNER,
  CM_RAW_FEB,
} from '../../data/cmFebData';
import {
  chartBaseOptions,
  EmptyState,
  ChipStrip,
  formatLakhs,
  formatRupees,
  InsightCard,
  KpiCard,
  palette,
  PriorityBadge,
  SearchField,
  SectionTitle,
  SortButton,
  InsightBanner,
} from '../dashboardPrimitives';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const CIRCLE_OPTIONS = [
  { value: 'ALL', label: 'All Circles' },
  { value: 'HP', label: 'HP' },
  { value: 'KS', label: 'KS' },
  { value: 'JA', label: 'JA' },
  { value: 'UW', label: 'UW' },
];

const DOMAIN_OPTIONS = [
  { value: 'ALL', label: 'Both Domains' },
  { value: 'Intercity', label: 'Intercity' },
  { value: 'Intracity', label: 'Intracity' },
];

const KPI_KEYS = [
  { label: 'FTKM', key: 'ftkm_s', max: 15 },
  { label: 'MTTR', key: 'mttr_s', max: 15 },
  { label: 'TTR%', key: 'ttr_s', max: 15 },
  { label: 'Temp Fault', key: 'tf_s', max: 15 },
  { label: 'T2P', key: 't2p_s', max: 10 },
  { label: 'GIS', key: 'gis_s', max: 10 },
  { label: 'Hi-Loss', key: 'hl_s', max: 10 },
  { label: 'Pat 1.8A', key: 'p1a_s', max: 5 },
  { label: 'Pat 1.8B', key: 'p1b_s', max: 5 },
];

function penaltyPctForScore(score) {
  if (score >= 90) return 0;
  if (score >= 85) return -2;
  if (score >= 80) return -3;
  if (score >= 75) return -4;
  if (score >= 70) return -5;
  if (score >= 65) return -6;
  return -7.5;
}

function formatSignedRupees(value) {
  if (value > 0) return `+${formatRupees(value)}`;
  if (value < 0) return `-${formatRupees(Math.abs(value))}`;
  return '₹0';
}

function formatSignedLakhs(value) {
  if (value > 0) return `+${formatLakhs(value)}`;
  if (value < 0) return `-${formatLakhs(Math.abs(value))}`;
  return '₹0.00L';
}

function totalScoreColor(score) {
  if (score >= 90) return palette.success;
  if (score >= 75) return palette.accent;
  if (score >= 65) return palette.warning;
  return palette.danger;
}

function scoreCell(score, max) {
  const tone = score === max
    ? { backgroundColor: '#DCFCE7', color: '#166534' }
    : score === 0
      ? { backgroundColor: '#FEE2E2', color: '#991B1B' }
      : { backgroundColor: '#FEF9C3', color: '#92400E' };
  return (
    <span
      className="mono-cell inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-semibold"
      style={tone}
    >
      {score}
    </span>
  );
}

function estimateRecoverable(row) {
  const uplift = (row.p1a_s === 0 ? 5 : 0) + (row.gis_s === 0 ? 10 : 0);
  if (!uplift) return 0;

  const currentPct = Math.abs(penaltyPctForScore(row.score));
  const improvedPct = Math.abs(penaltyPctForScore(Math.min(row.score + uplift, 100)));
  if (improvedPct >= currentPct || currentPct === 0) return 0;

  const baseValue = Math.abs(row.pen_rs) / (currentPct / 100);
  return Math.round(baseValue * ((currentPct - improvedPct) / 100));
}

function summarySortValue(row, key) {
  switch (key) {
    case 'circle':
    case 'mp':
      return row[key];
    default:
      return row[key] ?? 0;
  }
}

function quickWinMatchesFilter(item, activeCircle, activeDomain) {
  const match = item.mp.match(/^(.*) \((Inter|Intra)\)$/);
  if (!match) return true;
  const [, mp, domainShort] = match;
  const domain = domainShort === 'Inter' ? 'Intercity' : 'Intracity';
  const row = CM_RAW.find((entry) => entry.mp === mp && entry.dom === domain);
  if (!row) return false;
  if (activeCircle !== 'ALL' && row.circle !== activeCircle) return false;
  if (activeDomain !== 'ALL' && row.dom !== activeDomain) return false;
  return true;
}

const FEB_KPI_KEYS = [
  { label: 'FTKM', key: 'ftkm_s', max: 15 },
  { label: 'MTTR', key: 'mttr_s', max: 15 },
  { label: 'TTR%', key: 'ttr_s', max: 15 },
  { label: 'Temp Fault', key: 'tf_s', max: 15 },
  { label: 'T2P', key: 't2p_s', max: 10 },
  { label: 'GIS Update', key: 'gis_s', max: 10 },
  { label: 'High Loss', key: 'hl_s', max: 10 },
  { label: 'Pat 1.8A', key: 'p1a_s', max: 5 },
  { label: 'Pat 1.8B', key: 'p1b_s', max: 5 },
];

function febScoreCellMax(row, key, max) {
  if (key === 't2p_s' || key === 'hl_s') {
    return row.dom === 'Intracity' ? 15 : 10;
  }
  return max;
}

function CMPenaltyFeb() {
  const [activeCircle, setActiveCircle] = useState('ALL');
  const [activeDomain, setActiveDomain] = useState('ALL');
  const [summarySort, setSummarySort] = useState({ key: 'net', dir: 'asc' });
  const [heatmapSearch, setHeatmapSearch] = useState('');
  const [summarySearch, setSummarySearch] = useState('');

  const filteredRows = useMemo(() => {
    return CM_RAW_FEB.filter((row) => (
      (activeCircle === 'ALL' || row.circle === activeCircle)
      && (activeDomain === 'ALL' || row.dom === activeDomain)
    ));
  }, [activeCircle, activeDomain]);

  const intercityRows = useMemo(() => filteredRows.filter((row) => row.dom === 'Intercity'), [filteredRows]);

  const mpSummaryBase = useMemo(() => {
    const mps = [...new Set(filteredRows.map((row) => row.mp))];
    return mps.map((mp) => {
      const rows = filteredRows.filter((row) => row.mp === mp);
      const interScore = rows.find((row) => row.dom === 'Intercity')?.score ?? null;
      const intraScore = rows.find((row) => row.dom === 'Intracity')?.score ?? null;
      const gross = rows.reduce((sum, row) => sum + row.pen_rs, 0);
      const reward = rows.reduce((sum, row) => sum + row.ftkm_rwd, 0);
      return {
        circle: rows[0]?.circle ?? '-',
        mp,
        km: rows.reduce((sum, row) => sum + row.km, 0),
        faults: rows.reduce((sum, row) => sum + row.faults, 0),
        ftkmValue: rows.find((row) => row.dom === 'Intercity')?.ftkm_v ?? 0,
        interScore,
        intraScore,
        gross,
        reward,
        net: gross + reward,
      };
    });
  }, [filteredRows]);

  const summaryRows = useMemo(() => {
    const rows = [...mpSummaryBase];
    rows.sort((left, right) => {
      const leftValue = summarySortValue(left, summarySort.key);
      const rightValue = summarySortValue(right, summarySort.key);
      if (typeof leftValue === 'string') {
        return summarySort.dir === 'asc'
          ? leftValue.localeCompare(rightValue)
          : rightValue.localeCompare(leftValue);
      }
      return summarySort.dir === 'asc' ? leftValue - rightValue : rightValue - leftValue;
    });

    if (!summarySearch.trim()) return rows;

    const query = summarySearch.toLowerCase();
    return rows.filter((row) => row.mp.toLowerCase().includes(query) || row.circle.toLowerCase().includes(query));
  }, [mpSummaryBase, summarySearch, summarySort]);

  const heatmapRows = useMemo(() => {
    if (!heatmapSearch.trim()) return filteredRows;
    const query = heatmapSearch.toLowerCase();
    return filteredRows.filter((row) => row.mp.toLowerCase().includes(query) || row.dom.toLowerCase().includes(query));
  }, [filteredRows, heatmapSearch]);

  const totals = useMemo(() => {
    const grossPenalty = filteredRows.reduce((sum, row) => sum + row.pen_rs, 0);
    const reward = filteredRows.reduce((sum, row) => sum + row.ftkm_rwd, 0);
    const patrollerFails = filteredRows.filter((row) => row.p1a_s === 0).length;
    const gisFails = filteredRows.filter((row) => row.gis_s === 0).length;
    const avgInterScore = intercityRows.length
      ? intercityRows.reduce((sum, row) => sum + row.score, 0) / intercityRows.length
      : 0;
    const bestMp = [...mpSummaryBase].sort((left, right) => right.net - left.net)[0] ?? null;
    const worstMp = [...mpSummaryBase].sort((left, right) => left.net - right.net)[0] ?? null;

    return {
      grossPenalty,
      reward,
      netImpact: grossPenalty + reward,
      patrollerFails,
      gisFails,
      avgInterScore,
      bestMp,
      bestNetPositive: bestMp && bestMp.net > 0 ? bestMp : null,
      worstMp,
    };
  }, [filteredRows, intercityRows, mpSummaryBase]);

  const circlePenalty = useMemo(() => {
    return ['HP', 'KS', 'JA', 'UW'].map((circle) =>
      Math.abs(filteredRows.filter((row) => row.circle === circle).reduce((sum, row) => sum + row.pen_rs, 0)),
    );
  }, [filteredRows]);

  const circleReward = useMemo(() => {
    return ['HP', 'KS', 'JA', 'UW'].map((circle) =>
      filteredRows.filter((row) => row.circle === circle).reduce((sum, row) => sum + row.ftkm_rwd, 0),
    );
  }, [filteredRows]);

  const tierBuckets = useMemo(() => {
    const buckets = [
      { label: '< 65', pen: '-7.5%', color: palette.danger, items: [] },
      { label: '65-69', pen: '-6%', color: palette.orange, items: [] },
      { label: '70-74', pen: '-5%', color: palette.warning, items: [] },
      { label: '75-79', pen: '-4%', color: '#84CC16', items: [] },
      { label: '≥ 90', pen: '0%', color: palette.success, items: [] },
    ];

    filteredRows.forEach((row) => {
      let index = 0;
      if (row.score >= 90) index = 4;
      else if (row.score >= 75) index = 3;
      else if (row.score >= 70) index = 2;
      else if (row.score >= 65) index = 1;
      const domainShort = row.dom === 'Intercity' ? 'Inter' : 'Intra';
      buckets[index].items.push(`${row.mp} (${domainShort})`);
    });

    return buckets;
  }, [filteredRows]);

  const lostVsAchieved = useMemo(() => {
    const count = Math.max(filteredRows.length, 1);
    return FEB_KPI_KEYS.map((item) => {
      const achieved = filteredRows.reduce((sum, row) => sum + row[item.key], 0) / count;
      return {
        ...item,
        achieved,
        lost: item.max - achieved,
      };
    });
  }, [filteredRows]);

  const scoreChartLabels = CM_FEB_MPS;
  const ftkmRows = useMemo(() => [...intercityRows].sort((left, right) => right.ftkm_v - left.ftkm_v), [intercityRows]);
  const grossByMpRows = useMemo(() => [...mpSummaryBase].sort((left, right) => left.net - right.net), [mpSummaryBase]);
  const quickWins = useMemo(() => {
    return CM_FEB_QUICK_WINS.filter((row) => (
      (activeCircle === 'ALL' || row.circle === activeCircle)
      && (activeDomain === 'ALL' || row.domain === activeDomain)
    ));
  }, [activeCircle, activeDomain]);

  const updateSummarySort = (key) => {
    setSummarySort((current) => ({
      key,
      dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  if (!filteredRows.length) {
    return (
      <div className="space-y-6">
        <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: CIRCLE_OPTIONS }, { label: 'Type', value: activeDomain, onChange: setActiveDomain, options: DOMAIN_OPTIONS }]} />
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: CIRCLE_OPTIONS }, { label: 'Type', value: activeDomain, onChange: setActiveDomain, options: DOMAIN_OPTIONS }]} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <KpiCard
          label="Gross CM Penalty"
          value="-"
          sub="Before FTKM reward offset"
          dot={palette.danger}
          valueColor={palette.slateDeep}
        />
        <KpiCard
          label="FTKM Reward Recovered"
          value="-"
          sub="Pending billing finalisation"
          dot={palette.success}
          valueColor={palette.slateDeep}
        />
        <KpiCard
          label="Net CM Loss"
          value="-"
          sub="After offsetting FTKM rewards"
          dot={palette.orange}
          valueColor={palette.slateDeep}
        />
        <KpiCard
          label="Patroller 1.8A Failures"
          value={`${totals.patrollerFails}/${filteredRows.length || 0}`}
          sub="5 pts lost per row - unchanged from Dec"
          dot={palette.danger}
          valueColor={palette.danger}
        />
        <KpiCard
          label="GIS Update Failures"
          value={`${totals.gisFails}/${filteredRows.length || 0}`}
          sub="10 pts lost per row"
          dot={palette.warning}
          valueColor={palette.warning}
        />
        <KpiCard
          label="Recoverable (GIS+Pat Fix)"
          value="-"
          sub="Pending billing finalisation"
          dot={palette.accent}
          valueColor={palette.slateDeep}
        />
        <KpiCard
          label="Avg KPI Score (Intercity)"
          value={totals.avgInterScore.toFixed(1)}
          sub="Zone-wide intercity average"
          dot={palette.accent}
          valueColor={palette.navy}
        />
        <KpiCard
          label="Worst MP (Net)"
          value={totals.worstMp?.mp ?? '-'}
          sub="Highest net CM loss after FTKM"
          dot={palette.danger}
          valueColor={palette.danger}
        />
        <KpiCard
          label="Best MP (Net)"
          value={totals.bestNetPositive?.mp ?? '-'}
          sub="Only MP with net FTKM gain"
          dot={palette.success}
          valueColor={totals.bestNetPositive ? palette.success : palette.slateDeep}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Gross Penalty vs FTKM Reward by Circle</SectionTitle>
          <p className="dashboard-note">Billing is pending for Feb'26, so the current shell renders ₹0 values for gross penalty and FTKM reward.</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: ['HP', 'KS', 'JA', 'UW'],
                datasets: [
                  {
                    label: 'Gross Penalty',
                    data: circlePenalty,
                    backgroundColor: palette.danger,
                    borderRadius: 4,
                  },
                  {
                    label: 'FTKM Reward',
                    data: circleReward,
                    backgroundColor: palette.success,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => ` ${formatRupees(context.raw)}`,
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `₹${Math.round(value / 1000)}K`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.navy}>Gross Penalty by MP</SectionTitle>
          <p className="dashboard-note">Both domains stay rolled up at MP level while Feb billing remains pending, so the financial bars render as ₹0.</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: grossByMpRows.map((row) => row.mp),
                datasets: [
                  {
                    label: 'Penalty',
                    data: grossByMpRows.map((row) => Math.abs(row.gross)),
                    backgroundColor: palette.danger,
                    borderRadius: 4,
                  },
                  {
                    label: 'FTKM Reward',
                    data: grossByMpRows.map((row) => row.reward),
                    backgroundColor: palette.success,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => ` ${formatRupees(context.raw)}`,
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `₹${Math.round(value / 1000)}K`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.warning}>MP-Domain Penalty Tier Distribution</SectionTitle>
          <p className="dashboard-note">Only tiers with actual rows remain visible, matching the rendered Feb dashboard behavior.</p>
          <div style={{ height: 220 }}>
            <Bar
              data={{
                labels: tierBuckets.map((bucket) => `${bucket.label} (${bucket.pen})`),
                datasets: [
                  {
                    label: 'MP-domain combinations',
                    data: tierBuckets.map((bucket) => bucket.items.length),
                    backgroundColor: tierBuckets.map((bucket) => bucket.color),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  tooltip: {
                    callbacks: {
                      title: (items) => tierBuckets[items[0].dataIndex]?.label ?? '',
                      label: (context) => ` ${context.raw} MP-domain combinations`,
                      afterBody: (items) => {
                        const bucket = tierBuckets[items[0].dataIndex];
                        return bucket ? ['', 'MPs in this tier:', ...bucket.items.map((item) => `  • ${item}`)] : [];
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                    grid: { color: palette.border },
                    border: { display: false },
                  },
                },
              }}
            />
          </div>
          <div className="mt-4 space-y-2">
            {tierBuckets.map((bucket) => (
              <div key={`${bucket.label}-${bucket.pen}`} className="flex items-start gap-3 text-[11px] leading-5 text-slate-500">
                <span
                  className="inline-flex min-w-[76px] flex-shrink-0 justify-center rounded-lg px-2 py-1 font-semibold"
                  style={{ backgroundColor: `${bucket.color}18`, color: bucket.color }}
                >
                  {bucket.label}
                  <span className="ml-1">{bucket.pen}</span>
                </span>
                <span>{bucket.items.join(' · ')}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>KPI Points Lost by Category</SectionTitle>
          <p className="dashboard-note">Average score achieved versus points lost across all MP-domain rows in the active Feb scope.</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: lostVsAchieved.map((item) => item.label),
                datasets: [
                  {
                    label: 'Avg Score Achieved',
                    data: lostVsAchieved.map((item) => item.achieved),
                    backgroundColor: palette.success,
                    borderRadius: 4,
                    stack: 'score',
                  },
                  {
                    label: 'Avg Points Lost',
                    data: lostVsAchieved.map((item) => item.lost),
                    backgroundColor: palette.danger,
                    borderRadius: 4,
                    stack: 'score',
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    stacked: true,
                    max: 16,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Avg KPI Score by MP</SectionTitle>
          <p className="dashboard-note">Intercity and intracity scores remain split so the domain gap stays visible in the Feb view.</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: scoreChartLabels,
                datasets: [
                  {
                    label: 'Intercity',
                    data: scoreChartLabels.map((mp) => filteredRows.find((row) => row.mp === mp && row.dom === 'Intercity')?.score ?? null),
                    backgroundColor: palette.navy,
                    borderRadius: 4,
                  },
                  {
                    label: 'Intracity',
                    data: scoreChartLabels.map((mp) => filteredRows.find((row) => row.mp === mp && row.dom === 'Intracity')?.score ?? null),
                    backgroundColor: palette.accent,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: {
                      autoSkip: false,
                      color: palette.slateDeep,
                      font: { family: 'Inter', size: 10, weight: '500' },
                    },
                  },
                  y: {
                    min: 0,
                    max: 100,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>FTKM (Faults per 1000 KM)</SectionTitle>
          <p className="dashboard-note">Intercity FTKM only. Higher is operationally worse.</p>
          <div style={{ height: 252 }}>
            {ftkmRows.length ? (
              <Bar
                data={{
                  labels: ftkmRows.map((row) => row.mp),
                  datasets: [
                    {
                      data: ftkmRows.map((row) => row.ftkm_v),
                      backgroundColor: ftkmRows.map((row) => (
                        row.ftkm_v > 10 ? palette.danger : row.ftkm_v > 5 ? palette.warning : palette.success
                      )),
                      borderRadius: 4,
                    },
                  ],
                }}
                options={{
                  ...chartBaseOptions,
                  scales: {
                    x: {
                      grid: { display: false },
                      border: { display: false },
                      ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                    },
                    y: {
                      grid: { color: palette.border },
                      border: { display: false },
                      ticks: {
                        color: palette.slateDeep,
                        font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                      },
                    },
                  },
                }}
              />
            ) : (
              <EmptyState title="No data for selected filters" className="h-full min-h-0" />
            )}
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <SectionTitle dot={palette.warning}>KPI Score Map</SectionTitle>
            <p className="dashboard-note mb-0">
              {`Significant score decline vs Dec'25. ${filteredRows.length} MP-domain rows × 9 KPI categories · Patroller 1.8A: ${totals.patrollerFails}/${filteredRows.length || 0} zeros · GIS: ${totals.gisFails}/${filteredRows.length || 0} zeros · Avg Intercity Score: ${totals.avgInterScore.toFixed(1)} (Dec: ${CM_FEB_DEC_INTERCITY_AVG})`}
            </p>
          </div>
          <SearchField value={heatmapSearch} onChange={setHeatmapSearch} placeholder="Search MPs..." className="sm:max-w-xs" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-slate-500">
          <span className="summary-chip bg-emerald-100 text-emerald-700">Full points</span>
          <span className="summary-chip bg-red-100 text-red-700">Zero</span>
          <span className="summary-chip bg-amber-100 text-amber-700">Partial</span>
        </div>
        {heatmapRows.length ? (
          <div className="mt-4 table-scroll-shell">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th className="sticky-col" style={{ left: 0, minWidth: '96px' }}>Circle</th>
                  <th className="sticky-col" style={{ left: 96, minWidth: '180px' }}>MP</th>
                  <th>Domain</th>
                  {FEB_KPI_KEYS.map((item) => (
                    <th key={item.key} className="text-center">
                      {item.label}
                      <div className="mt-0.5 text-[9px] font-normal tracking-normal text-slate-400">
                        {item.key === 't2p_s' || item.key === 'hl_s' ? 'max 10/15' : `max ${item.max}`}
                      </div>
                    </th>
                  ))}
                  <th>Total</th>
                  <th>Penalty ₹</th>
                  <th>FTKM Reward ₹</th>
                  <th>Net ₹</th>
                </tr>
              </thead>
              <tbody>
                {heatmapRows.map((row) => {
                  const totalTone = totalScoreColor(row.score);
                  return (
                    <tr key={`${row.mp}-${row.dom}`}>
                      <td className="sticky-col" style={{ left: 0, minWidth: '96px' }}>
                        <span
                          className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                          style={{ backgroundColor: `${CIRCLE_COLORS[row.circle]}16`, color: CIRCLE_COLORS[row.circle] }}
                        >
                          {row.circle}
                        </span>
                      </td>
                      <td className="sticky-col font-semibold text-slate-700" style={{ left: 96, minWidth: '180px' }}>
                        {row.mp}
                      </td>
                      <td>{row.dom}</td>
                      {FEB_KPI_KEYS.map((item) => (
                        <td key={`${row.mp}-${row.dom}-${item.key}`} className="text-center">
                          {scoreCell(row[item.key], febScoreCellMax(row, item.key, item.max))}
                        </td>
                      ))}
                      <td>
                        <span className="mono-cell inline-flex rounded-lg px-2 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: totalTone }}>
                          {row.score}
                        </span>
                      </td>
                      <td className="mono-cell font-semibold text-red-500">-</td>
                      <td className="mono-cell font-semibold text-emerald-600">-</td>
                      <td className="mono-cell font-semibold text-slate-600">₹0</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[220px]" />
        )}
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <SectionTitle dot={palette.navy}>MP-Level Summary</SectionTitle>
            <p className="dashboard-note mb-0">Billing is pending finalisation for Feb'26. Use the headers to reorder by scope, FTKM, or score.</p>
          </div>
          <SearchField value={summarySearch} onChange={setSummarySearch} placeholder="Search MPs..." className="sm:max-w-xs" />
        </div>
        {summaryRows.length ? (
          <div className="mt-4 table-scroll-shell">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th><SortButton label="Circle" sortKey="circle" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="MP" sortKey="mp" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Scope KM" sortKey="km" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Faults" sortKey="faults" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="FTKM" sortKey="ftkmValue" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Inter Score" sortKey="interScore" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Intra Score" sortKey="intraScore" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Gross Penalty ₹" sortKey="gross" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="FTKM Reward ₹" sortKey="reward" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Net Impact ₹" sortKey="net" sortState={summarySort} onSort={updateSummarySort} /></th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((row) => (
                  <tr key={row.mp}>
                    <td>
                      <span
                        className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                        style={{ backgroundColor: `${CIRCLE_COLORS[row.circle]}16`, color: CIRCLE_COLORS[row.circle] }}
                      >
                        {row.circle}
                      </span>
                    </td>
                    <td className="font-semibold text-slate-700">{row.mp}</td>
                    <td className="mono-cell">{row.km.toFixed(0)} KM</td>
                    <td className="mono-cell">{row.faults}</td>
                    <td className="mono-cell" style={{ color: row.ftkmValue > 10 ? palette.danger : row.ftkmValue > 5 ? palette.warning : palette.success }}>
                      {row.ftkmValue ? row.ftkmValue.toFixed(1) : 'Zero'}
                    </td>
                    <td>{row.interScore !== null ? <span className="mono-cell inline-flex rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold" style={{ color: totalScoreColor(row.interScore) }}>{row.interScore}</span> : '-'}</td>
                    <td>{row.intraScore !== null ? <span className="mono-cell inline-flex rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold" style={{ color: totalScoreColor(row.intraScore) }}>{row.intraScore}</span> : '-'}</td>
                    <td className="mono-cell font-semibold text-red-500">₹0</td>
                    <td className="mono-cell font-semibold text-emerald-600">-</td>
                    <td className="mono-cell font-semibold text-slate-600">₹0</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[180px]" />
        )}
      </div>

      <InsightBanner chips={CM_FEB_INSIGHT_CHIPS} />

      <div className="grid grid-cols-1 gap-4">
        {CM_FEB_INSIGHTS.map((insight, index) => (
          <InsightCard
            key={insight.title}
            tone={insight.tone}
            title={insight.title}
            exposure={insight.exposure}
            body={insight.body}
            actionTitle={insight.actionTitle}
            action={insight.action}
          >
            {index === 4 ? (
              <div className="mt-4 table-scroll-shell-bordered">
                <table className="evidence-table">
                  <thead>
                    <tr>
                      <th>MP (Domain)</th>
                      <th>Current Score</th>
                      <th>Target Tier</th>
                      <th>Easiest Lever</th>
                      <th>Score After</th>
                      <th>Penalty % Saving</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quickWins.length ? (
                      quickWins.map((row) => (
                        <tr key={row.displayMp}>
                          <td className="font-semibold text-slate-700">{row.displayMp}</td>
                          <td className="mono-cell">{row.score}</td>
                          <td className="mono-cell">{row.target}</td>
                          <td className="whitespace-normal text-[11px] leading-5 text-slate-600">{row.lever}</td>
                          <td className="mono-cell">{row.scoreAfter}</td>
                          <td className="mono-cell font-semibold text-blue-600">{row.saving}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-slate-400">No quick-win rows match the current filters.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : null}
          </InsightCard>
        ))}
      </div>

      <div className="card-surface border border-emerald-200 bg-emerald-50/80 p-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-emerald-700">Recoverable Upside</div>
        <div className="mt-2 text-[13px] font-semibold leading-6 text-emerald-900">{CM_FEB_RECOVERABLE_BANNER}</div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.success}>Prioritised Action Plan</SectionTitle>
        <div className="table-scroll-shell">
          <table className="action-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Action</th>
                <th>MPs Impacted</th>
                <th>Owner</th>
                <th>Expected Score Lift</th>
              </tr>
            </thead>
            <tbody>
              {CM_FEB_ACTIONS.map((row) => (
                <tr key={row.action}>
                  <td><PriorityBadge priority={row.priority} /></td>
                  <td>
                    <div className="font-semibold text-slate-700">{row.action}</div>
                    <div className="mt-1 text-[11px] leading-5 text-slate-500">{row.detail}</div>
                  </td>
                  <td className="text-slate-600">{row.impacted}</td>
                  <td className="text-slate-600">{row.owner}</td>
                  <td className="text-[11px] leading-5 text-slate-600">{row.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function CMPenalty({ selectedMonth = "Dec'25" }) {
  const [activeCircle, setActiveCircle] = useState('ALL');
  const [activeDomain, setActiveDomain] = useState('ALL');
  const [summarySort, setSummarySort] = useState({ key: 'net', dir: 'asc' });
  const [heatmapSearch, setHeatmapSearch] = useState('');
  const [summarySearch, setSummarySearch] = useState('');

  const filteredRows = useMemo(() => {
    return CM_RAW.filter((row) => (
      (activeCircle === 'ALL' || row.circle === activeCircle)
      && (activeDomain === 'ALL' || row.dom === activeDomain)
    ));
  }, [activeCircle, activeDomain]);

  const intercityRows = useMemo(() => filteredRows.filter((row) => row.dom === 'Intercity'), [filteredRows]);
  const mps = useMemo(() => [...new Set(filteredRows.map((row) => row.mp))], [filteredRows]);

  const mpSummaryBase = useMemo(() => {
    return mps.map((mp) => {
      const rows = filteredRows.filter((row) => row.mp === mp);
      const interScore = rows.find((row) => row.dom === 'Intercity')?.score ?? null;
      const intraScore = rows.find((row) => row.dom === 'Intracity')?.score ?? null;
      const gross = rows.reduce((sum, row) => sum + row.pen_rs, 0);
      const reward = rows.reduce((sum, row) => sum + row.ftkm_rwd, 0);
      return {
        circle: rows[0]?.circle ?? '-',
        mp,
        km: rows.reduce((sum, row) => sum + row.km, 0),
        faults: rows.reduce((sum, row) => sum + row.faults, 0),
        ftkmValue: rows.find((row) => row.dom === 'Intercity')?.ftkm_v ?? 0,
        interScore,
        intraScore,
        gross,
        reward,
        net: gross + reward,
      };
    });
  }, [filteredRows, mps]);

  const summaryRows = useMemo(() => {
    const rows = [...mpSummaryBase];
    rows.sort((left, right) => {
      const leftValue = summarySortValue(left, summarySort.key);
      const rightValue = summarySortValue(right, summarySort.key);
      if (typeof leftValue === 'string') {
        return summarySort.dir === 'asc'
          ? leftValue.localeCompare(rightValue)
          : rightValue.localeCompare(leftValue);
      }
      return summarySort.dir === 'asc' ? leftValue - rightValue : rightValue - leftValue;
    });
    if (!summarySearch.trim()) return rows;
    const query = summarySearch.toLowerCase();
    return rows.filter((row) => row.mp.toLowerCase().includes(query) || row.circle.toLowerCase().includes(query));
  }, [mpSummaryBase, summarySearch, summarySort]);

  const heatmapRows = useMemo(() => {
    if (!heatmapSearch.trim()) return filteredRows;
    const query = heatmapSearch.toLowerCase();
    return filteredRows.filter((row) => row.mp.toLowerCase().includes(query) || row.dom.toLowerCase().includes(query));
  }, [filteredRows, heatmapSearch]);

  const totals = useMemo(() => {
    const grossPenalty = filteredRows.reduce((sum, row) => sum + row.pen_rs, 0);
    const reward = filteredRows.reduce((sum, row) => sum + row.ftkm_rwd, 0);
    const netImpact = grossPenalty + reward;
    const patrollerFails = filteredRows.filter((row) => row.p1a_s === 0).length;
    const gisFails = filteredRows.filter((row) => row.gis_s === 0).length;
    const recoverable = filteredRows.reduce((sum, row) => sum + estimateRecoverable(row), 0);
    const benchmarkRows = activeDomain === 'ALL' ? intercityRows : filteredRows;
    const avgScore = benchmarkRows.length
      ? benchmarkRows.reduce((sum, row) => sum + row.score, 0) / benchmarkRows.length
      : 0;
    const bestMp = [...mpSummaryBase].sort((left, right) => right.net - left.net)[0] ?? null;
    const worstMp = [...mpSummaryBase].sort((left, right) => left.net - right.net)[0] ?? null;

    return {
      grossPenalty,
      reward,
      netImpact,
      patrollerFails,
      gisFails,
      recoverable,
      avgScore,
      bestMp,
      worstMp,
    };
  }, [activeDomain, filteredRows, intercityRows, mpSummaryBase]);

  const circlePenalty = useMemo(() => {
    return ['HP', 'KS', 'JA', 'UW'].map((circle) =>
      Math.abs(filteredRows.filter((row) => row.circle === circle).reduce((sum, row) => sum + row.pen_rs, 0)),
    );
  }, [filteredRows]);

  const circleReward = useMemo(() => {
    return ['HP', 'KS', 'JA', 'UW'].map((circle) =>
      filteredRows.filter((row) => row.circle === circle).reduce((sum, row) => sum + row.ftkm_rwd, 0),
    );
  }, [filteredRows]);

  const tierBuckets = useMemo(() => {
    const buckets = [
      { label: '< 65', pen: '-7.5%', color: palette.danger, items: [] },
      { label: '65-69', pen: '-6%', color: palette.danger, items: [] },
      { label: '70-74', pen: '-5%', color: palette.warning, items: [] },
      { label: '75-79', pen: '-4%', color: palette.slate, items: [] },
      { label: '80-84', pen: '-3%', color: palette.accent, items: [] },
      { label: '85-89', pen: '-2%', color: palette.navy, items: [] },
      { label: '≥ 90', pen: '0%', color: palette.success, items: [] },
    ];

    filteredRows.forEach((row) => {
      let index = 0;
      if (row.score >= 90) index = 6;
      else if (row.score >= 85) index = 5;
      else if (row.score >= 80) index = 4;
      else if (row.score >= 75) index = 3;
      else if (row.score >= 70) index = 2;
      else if (row.score >= 65) index = 1;
      const domainShort = row.dom === 'Intercity' ? 'Inter' : 'Intra';
      buckets[index].items.push(`${row.mp} (${domainShort})`);
    });

    return buckets.filter((bucket) => bucket.items.length > 0);
  }, [filteredRows]);

  const lostVsAchieved = useMemo(() => {
    const count = Math.max(filteredRows.length, 1);
    return KPI_KEYS.map((item) => {
      const achieved = filteredRows.reduce((sum, row) => sum + row[item.key], 0) / count;
      return {
        ...item,
        achieved,
        lost: item.max - achieved,
      };
    });
  }, [filteredRows]);

  const ftkmRows = useMemo(() => [...intercityRows].sort((left, right) => right.ftkm_v - left.ftkm_v), [intercityRows]);
  const grossByMpRows = useMemo(() => [...mpSummaryBase].sort((left, right) => left.net - right.net), [mpSummaryBase]);
  const quickWins = useMemo(() => CM_QUICK_WINS.filter((item) => quickWinMatchesFilter(item, activeCircle, activeDomain)), [activeCircle, activeDomain]);

  const updateSummarySort = (key) => {
    setSummarySort((current) => ({
      key,
      dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const scoreChartLabels = mps;
  const scoreCardLabel = activeDomain === 'Intracity' ? 'Avg Intracity Score' : 'Avg Intercity Score';
  const netImpactColor = totals.netImpact > 0 ? palette.success : totals.netImpact < 0 ? palette.danger : palette.slateDeep;

  if (selectedMonth === "Feb'26") {
    return <CMPenaltyFeb />;
  }

  if (!filteredRows.length) {
    return (
      <div className="space-y-6">
        <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: CIRCLE_OPTIONS }, { label: 'Type', value: activeDomain, onChange: setActiveDomain, options: DOMAIN_OPTIONS }]} />
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: CIRCLE_OPTIONS }, { label: 'Type', value: activeDomain, onChange: setActiveDomain, options: DOMAIN_OPTIONS }]} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <KpiCard
          label="Gross CM Penalty"
          value={formatLakhs(Math.abs(totals.grossPenalty))}
          sub="Penalty before FTKM reward offsets"
          dot={palette.danger}
          valueColor={palette.danger}
          pill={`${filteredRows.length} MP-domain rows`}
        />
        <KpiCard
          label="FTKM Reward Recovered"
          value={formatLakhs(totals.reward)}
          sub="Offset recovered through qualifying FTKM performance"
          dot={palette.success}
          valueColor={palette.success}
          pill="Intercity only"
        />
        <KpiCard
          label="Net CM Impact"
          value={formatSignedLakhs(totals.netImpact)}
          sub="Residual CM outcome after reward recovery"
          dot={netImpactColor}
          valueColor={netImpactColor}
          pill={totals.netImpact > 0 ? 'Net positive' : 'Net loss'}
        />
        <KpiCard
          label="Patroller 1.8A Fails"
          value={`${totals.patrollerFails}/${filteredRows.length || 0}`}
          sub="Rows scoring zero on the patroller coverage KPI"
          dot={palette.danger}
          valueColor={palette.danger}
          pill="Universal failure pattern"
        />
        <KpiCard
          label="GIS Update Fails"
          value={`${totals.gisFails}/${filteredRows.length || 0}`}
          sub="Rows scoring zero on GIS compliance"
          dot={palette.warning}
          valueColor={palette.warning}
          pill="Back-office recoverable"
        />
        <KpiCard
          label="Recoverable (GIS+Pat Fix)"
          value={formatLakhs(totals.recoverable)}
          sub="Estimated gain from GIS + Patroller 1.8A correction"
          dot={palette.accent}
          valueColor={palette.navy}
          pill="Tier improvement estimate"
        />
        <KpiCard
          label={scoreCardLabel}
          value={totals.avgScore ? totals.avgScore.toFixed(1) : '-'}
          sub={activeDomain === 'ALL' ? 'Weighted average score for intercity rows in scope' : `Weighted average score for ${activeDomain.toLowerCase()} rows in scope`}
          dot={palette.accent}
          valueColor={palette.navy}
          pill={activeDomain === 'ALL' ? 'Intercity benchmark' : activeDomain}
        />
        <KpiCard
          label="Worst MP"
          value={totals.worstMp?.mp ?? '-'}
          sub={totals.worstMp ? `${formatSignedLakhs(totals.worstMp.net)} net CM impact` : 'No MP in scope'}
          dot={palette.danger}
          valueColor={palette.danger}
          pill={totals.worstMp?.circle ?? null}
        />
        <KpiCard
          label="Best MP"
          value={totals.bestMp?.mp ?? '-'}
          sub={totals.bestMp ? `${formatSignedLakhs(totals.bestMp.net)} net outcome` : 'No MP in scope'}
          dot={palette.success}
          valueColor={palette.success}
          pill={totals.bestMp?.circle ?? null}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Gross Penalty vs FTKM Reward by Circle</SectionTitle>
          <p className="dashboard-note">Grouped view of penalty leakage versus reward recovery by circle.</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: ['HP', 'KS', 'JA', 'UW'],
                datasets: [
                  {
                    label: 'Gross Penalty',
                    data: circlePenalty,
                    backgroundColor: palette.danger,
                    borderRadius: 4,
                  },
                  {
                    label: 'FTKM Reward',
                    data: circleReward,
                    backgroundColor: palette.success,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => ` ${formatRupees(context.raw)}`,
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `₹${Math.round(value / 1000)}K`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.navy}>Gross Penalty by MP</SectionTitle>
          <p className="dashboard-note">Both domains rolled up at MP level, sorted by net CM impact.</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: grossByMpRows.map((row) => row.mp),
                datasets: [
                  {
                    label: 'Penalty',
                    data: grossByMpRows.map((row) => Math.abs(row.gross)),
                    backgroundColor: palette.danger,
                    borderRadius: 4,
                  },
                  {
                    label: 'FTKM Reward',
                    data: grossByMpRows.map((row) => row.reward),
                    backgroundColor: palette.success,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => ` ${formatRupees(context.raw)}`,
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `₹${Math.round(value / 1000)}K`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.warning}>MP-Domain Penalty Tier Distribution</SectionTitle>
          <p className="dashboard-note">Only tiers with actual rows remain visible, matching the source dashboard.</p>
          <div style={{ height: 220 }}>
            <Bar
              data={{
                labels: tierBuckets.map((bucket) => `${bucket.label} (${bucket.pen})`),
                datasets: [
                  {
                    label: 'MP-domain combinations',
                    data: tierBuckets.map((bucket) => bucket.items.length),
                    backgroundColor: tierBuckets.map((bucket) => bucket.color),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  tooltip: {
                    callbacks: {
                      title: (items) => tierBuckets[items[0].dataIndex]?.label ?? '',
                      label: (context) => ` ${context.raw} MP-domain combinations`,
                      afterBody: (items) => {
                        const bucket = tierBuckets[items[0].dataIndex];
                        return bucket ? ['', 'MPs in this tier:', ...bucket.items.map((item) => `  • ${item}`)] : [];
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                    grid: { color: palette.border },
                    border: { display: false },
                  },
                },
              }}
            />
          </div>
          <div className="mt-4 space-y-2">
            {tierBuckets.map((bucket) => (
              <div key={`${bucket.label}-${bucket.pen}`} className="flex items-start gap-3 text-[11px] leading-5 text-slate-500">
                <span
                  className="inline-flex min-w-[76px] flex-shrink-0 justify-center rounded-lg px-2 py-1 font-semibold"
                  style={{ backgroundColor: `${bucket.color}18`, color: bucket.color }}
                >
                  {bucket.label}
                  <span className="ml-1">{bucket.pen}</span>
                </span>
                <span>{bucket.items.join(' · ')}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>KPI Points Lost by Category</SectionTitle>
          <p className="dashboard-note">Average achieved points versus lost points by KPI category.</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: lostVsAchieved.map((item) => item.label),
                datasets: [
                  {
                    label: 'Avg Achieved',
                    data: lostVsAchieved.map((item) => item.achieved),
                    backgroundColor: palette.accent,
                    borderRadius: 4,
                    stack: 'score',
                  },
                  {
                    label: 'Avg Lost',
                    data: lostVsAchieved.map((item) => item.lost),
                    backgroundColor: palette.danger,
                    borderRadius: 4,
                    stack: 'score',
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    stacked: true,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Avg KPI Score by MP</SectionTitle>
          <p className="dashboard-note">Intercity and intracity scores remain split so the domain gap is visible.</p>
          <div style={{ height: 252 }}>
            <Bar
              data={{
                labels: scoreChartLabels,
                datasets: activeDomain === 'ALL'
                  ? [
                    {
                      label: 'Intercity',
                      data: scoreChartLabels.map((mp) => filteredRows.find((row) => row.mp === mp && row.dom === 'Intercity')?.score ?? null),
                      backgroundColor: palette.navy,
                      borderRadius: 4,
                    },
                    {
                      label: 'Intracity',
                      data: scoreChartLabels.map((mp) => filteredRows.find((row) => row.mp === mp && row.dom === 'Intracity')?.score ?? null),
                      backgroundColor: palette.accent,
                      borderRadius: 4,
                    },
                  ]
                  : [
                    {
                      label: activeDomain,
                      data: scoreChartLabels.map((mp) => filteredRows.find((row) => row.mp === mp)?.score ?? null),
                      backgroundColor: palette.accent,
                      borderRadius: 4,
                    },
                  ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    min: 0,
                    max: 100,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>FTKM (Faults per 1000 KM)</SectionTitle>
          <p className="dashboard-note">Intercity FTKM only. Higher is operationally worse.</p>
          <div style={{ height: 252 }}>
            {ftkmRows.length ? (
              <Bar
                data={{
                  labels: ftkmRows.map((row) => row.mp),
                  datasets: [
                    {
                      data: ftkmRows.map((row) => row.ftkm_v),
                      backgroundColor: ftkmRows.map((row) => (
                        row.ftkm_v > 10 ? palette.danger : row.ftkm_v > 5 ? palette.warning : palette.success
                      )),
                      borderRadius: 4,
                    },
                  ],
                }}
                options={{
                  ...chartBaseOptions,
                  scales: {
                    x: {
                      grid: { display: false },
                      border: { display: false },
                      ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                    },
                    y: {
                      grid: { color: palette.border },
                      border: { display: false },
                      ticks: {
                        color: palette.slateDeep,
                        font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                      },
                    },
                  },
                }}
              />
            ) : (
              <EmptyState title="No data for selected filters" className="h-full min-h-0" />
            )}
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <SectionTitle dot={palette.warning}>KPI Score Map</SectionTitle>
            <p className="dashboard-note mb-0">
              Two universal failures dominate the recoverable value: Patroller 1.8A zeros on {totals.patrollerFails}/{filteredRows.length || 0} rows and GIS zeros on {totals.gisFails}/{filteredRows.length || 0} rows.
            </p>
          </div>
          <SearchField value={heatmapSearch} onChange={setHeatmapSearch} placeholder="Search MPs..." className="sm:max-w-xs" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-slate-500">
          <span className="summary-chip bg-emerald-100 text-emerald-700">Full points</span>
          <span className="summary-chip bg-red-100 text-red-700">Zero</span>
          <span className="summary-chip bg-amber-100 text-amber-700">Partial</span>
        </div>
        {heatmapRows.length ? (
          <div className="mt-4 table-scroll-shell">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th className="sticky-col" style={{ left: 0, minWidth: '180px' }}>MP</th>
                  <th className="sticky-col" style={{ left: 180, minWidth: '128px' }}>Domain</th>
                  <th>Circle</th>
                  {KPI_KEYS.map((item) => (
                    <th key={item.key} className="text-center">
                      {item.label}
                      <div className="mt-0.5 text-[9px] font-normal tracking-normal text-slate-400">{`max ${item.max}`}</div>
                    </th>
                  ))}
                  <th>Total</th>
                  <th>Penalty</th>
                  <th>FTKM Reward</th>
                  <th>Net CM Impact</th>
                </tr>
              </thead>
              <tbody>
                {heatmapRows.map((row) => {
                const totalTone = totalScoreColor(row.score);
                const net = row.pen_rs + row.ftkm_rwd;
                const netColor = net < 0 ? palette.danger : net > 0 ? palette.success : palette.slateDeep;
                return (
                  <tr key={`${row.mp}-${row.dom}`}>
                    <td className="sticky-col font-semibold text-slate-700" style={{ left: 0, minWidth: '180px' }}>
                      {row.mp}
                    </td>
                    <td className="sticky-col" style={{ left: 180, minWidth: '128px' }}>{row.dom}</td>
                    <td>
                      <span
                        className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                        style={{ backgroundColor: `${CIRCLE_COLORS[row.circle]}16`, color: CIRCLE_COLORS[row.circle] }}
                      >
                        {row.circle}
                      </span>
                    </td>
                    {KPI_KEYS.map((item) => (
                      <td key={`${row.mp}-${row.dom}-${item.key}`} className="text-center">
                        {scoreCell(row[item.key], item.max)}
                      </td>
                    ))}
                    <td>
                      <span className="mono-cell inline-flex rounded-lg px-2 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: totalTone }}>
                        {row.score}
                      </span>
                    </td>
                    <td className="mono-cell font-semibold text-red-500">{row.pen_rs < 0 ? formatRupees(Math.abs(row.pen_rs)) : '-'}</td>
                    <td className="mono-cell font-semibold text-emerald-600">{row.ftkm_rwd > 0 ? formatRupees(row.ftkm_rwd) : '-'}</td>
                    <td className="mono-cell font-semibold" style={{ color: netColor }}>{formatSignedRupees(net)}</td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[220px]" />
        )}
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <SectionTitle dot={palette.navy}>MP-Level Summary</SectionTitle>
            <p className="dashboard-note mb-0">Sorted by net CM impact. Use the headers to reorder by scope, FTKM, or score.</p>
          </div>
          <SearchField value={summarySearch} onChange={setSummarySearch} placeholder="Search MPs..." className="sm:max-w-xs" />
        </div>
        {summaryRows.length ? (
          <div className="mt-4 table-scroll-shell">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th><SortButton label="Circle" sortKey="circle" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="MP" sortKey="mp" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Scope KM" sortKey="km" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Faults" sortKey="faults" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="FTKM Inter" sortKey="ftkmValue" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Inter Score" sortKey="interScore" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Intra Score" sortKey="intraScore" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Gross Penalty" sortKey="gross" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="FTKM Reward" sortKey="reward" sortState={summarySort} onSort={updateSummarySort} /></th>
                  <th><SortButton label="Net CM Impact" sortKey="net" sortState={summarySort} onSort={updateSummarySort} /></th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((row) => {
                const netColor = row.net < 0 ? palette.danger : row.net > 0 ? palette.success : palette.slateDeep;
                return (
                  <tr key={row.mp}>
                    <td>
                      <span
                        className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                        style={{ backgroundColor: `${CIRCLE_COLORS[row.circle]}16`, color: CIRCLE_COLORS[row.circle] }}
                      >
                        {row.circle}
                      </span>
                    </td>
                    <td className="font-semibold text-slate-700">{row.mp}</td>
                    <td className="mono-cell">{row.km.toFixed(0)} KM</td>
                    <td className="mono-cell">{row.faults}</td>
                    <td className="mono-cell" style={{ color: row.ftkmValue > 10 ? palette.danger : row.ftkmValue > 5 ? palette.warning : palette.success }}>
                      {row.ftkmValue ? row.ftkmValue.toFixed(1) : 'Zero'}
                    </td>
                    <td>{row.interScore !== null ? <span className="mono-cell inline-flex rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold" style={{ color: totalScoreColor(row.interScore) }}>{row.interScore}</span> : '-'}</td>
                    <td>{row.intraScore !== null ? <span className="mono-cell inline-flex rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-semibold" style={{ color: totalScoreColor(row.intraScore) }}>{row.intraScore}</span> : '-'}</td>
                    <td className="mono-cell font-semibold text-red-500">{row.gross < 0 ? formatRupees(Math.abs(row.gross)) : '₹0'}</td>
                    <td className="mono-cell font-semibold text-emerald-600">{row.reward ? formatRupees(row.reward) : '-'}</td>
                    <td className="mono-cell font-semibold" style={{ color: netColor }}>{formatSignedRupees(row.net)}</td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[180px]" />
        )}
      </div>

      <InsightBanner chips={CM_INSIGHT_CHIPS} />

      <div className="grid grid-cols-1 gap-4">
        {CM_INSIGHTS.map((insight, index) => (
          <InsightCard
            key={insight.title}
            tone={insight.tone}
            title={insight.title}
            exposure={insight.exposure}
            body={insight.body}
            actionTitle={insight.actionTitle}
            action={insight.action}
          >
            {index === 4 ? (
              <div className="mt-4 table-scroll-shell-bordered">
                <table className="evidence-table">
                  <thead>
                    <tr>
                      <th>MP-Domain</th>
                      <th>Current Tier</th>
                      <th>Target Tier</th>
                      <th>Primary Lever</th>
                      <th>Score After</th>
                      <th>Saving</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quickWins.length ? (
                      quickWins.map((row) => (
                        <tr key={row.mp}>
                          <td className="font-semibold text-slate-700">{row.mp}</td>
                          <td className="mono-cell">{row.score}</td>
                          <td className="mono-cell">{row.target}</td>
                          <td className="whitespace-normal text-[11px] leading-5 text-slate-600">{row.lever}</td>
                          <td className="mono-cell">{row.scoreAfter}</td>
                          <td className="mono-cell font-semibold text-blue-600">{row.saving}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-slate-400">No quick-win rows match the current filters.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : null}
          </InsightCard>
        ))}
      </div>

      <div className="card-surface border border-emerald-200 bg-emerald-50/80 p-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-emerald-700">Recoverable Upside</div>
        <div className="mt-2 text-[13px] font-semibold leading-6 text-emerald-900">{CM_RECOVERABLE_BANNER}</div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.success}>Prioritised Action Plan</SectionTitle>
        <div className="table-scroll-shell">
          <table className="action-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Action</th>
                <th>Impacted Scope</th>
                <th>Owner</th>
                <th>Impact</th>
              </tr>
            </thead>
            <tbody>
              {CM_ACTIONS.map((row) => (
                <tr key={row.action}>
                  <td><PriorityBadge priority={row.priority} /></td>
                  <td>
                    <div className="font-semibold text-slate-700">{row.action}</div>
                    <div className="mt-1 text-[11px] leading-5 text-slate-500">{row.detail}</div>
                  </td>
                  <td className="text-slate-600">{row.impacted}</td>
                  <td className="text-slate-600">{row.owner}</td>
                  <td className="text-[11px] leading-5 text-slate-600">{row.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
